import { Box, Input, InputAdornment, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import './style.css';
import NewTimeRange from '../NewTimeRange';
import { Clear } from '@mui/icons-material';

export const TimeRangePicker = ({
  filterType,
  filters,
  setFilters,
  setContextFilters,
  contextFilters
}) => {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [displayedValue, setDisplayedValue] = useState('');
  const [error, setError] = useState(false);
  const [selectedRange, setSelectedRange] = useState({
    time_after: undefined,
    time_before: undefined
  });

  const handleClick = () => {
    setPickerOpen(true);
  };

  const disableTimeRange = useMemo(() => {
    return !filters?.single_date;
  }, [filters]);

  const handleChange = useCallback(
    (e, key) => {
      if (e !== selectedRange?.[key]) {
        const formattedValue = e.format(`HH:mm:ss`);
        setSelectedRange({
          ...selectedRange,
          [key]: formattedValue
        });
      }
    },
    [selectedRange, setSelectedRange]
  );

  useEffect(() => {
    if (selectedRange.time_before && selectedRange.time_after) {
      const time_before = selectedRange.time_before;
      const time_after = selectedRange.time_after;

      if (
        time_after.split(':')[1] !== filters?.time_after?.split(':')[1] ||
        time_before.split(':')[1] !== filters?.time_before?.split(':')[1]
      ) {
        setPickerOpen(false);
      }

      setFilters({
        ...filters,
        time_after,
        time_before
      });
      setDisplayedValue(
        `From ${selectedRange.time_after.slice(0, -3)} to ${selectedRange.time_before.slice(0, -3)}`
      );
    }
  }, [selectedRange]);

  useEffect(() => {
    if (!Object.keys(filters)?.length && filterType) {
      setSelectedRange({
        time_after: undefined,
        time_before: undefined
      });
      setDisplayedValue('');
    }
    if (filters?.time_after && filters?.time_before) {
      setDisplayedValue(
        `From ${filters.time_after.slice(0, -3)} to ${filters.time_before.slice(0, -3)}`
      );
    }
  }, [filters, filterType, setSelectedRange]);

  const handleClear = useCallback(() => {
    const newFilters = {
      ...filters
    };
    delete newFilters?.time_after;
    delete newFilters?.time_before;
    setFilters(newFilters);

    const newContextFilters = {
      ...contextFilters
    };
    delete newContextFilters?.time_after;
    delete newContextFilters?.time_before;
    setContextFilters(newContextFilters);

    setSelectedRange({
      time_after: undefined,
      time_before: undefined
    });
    setDisplayedValue('');
    setPickerOpen(false);
  }, [filters, setFilters, setSelectedRange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        mt: 2,
        pl: 2,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          bgcolor: '#E2E2E2',
          padding: '4px 12px',
          textAlign: 'center',
          borderRadius: '4px 0 0 4px'
        }}
      >
        <Typography color="#00010E" fontSize={14} fontWeight={200} lineHeight="22px">
          {t(`filters.${filterType}`)}
        </Typography>
      </Box>
      <Input
        displayempty="true"
        id="filter-simple-select"
        onFocus={handleClick}
        disableUnderline
        placeholder="Select time"
        autoComplete="off"
        disabled={disableTimeRange}
        endAdornment={
          <InputAdornment
            sx={{
              cursor: 'pointer',
              position: 'absolute',
              display: disableTimeRange ? 'none' : 'flex',
              right: 10,
              width: 15
            }}
          >
            <Clear
              color="#9B959F"
              fontSize="14px"
              sx={{ cursor: 'pointer' }}
              onClick={handleClear}
            />
          </InputAdornment>
        }
        sx={{
          '& .MuiInputBase-input': {
            padding: '3px 0 3px 12px',
            minWidth: '50px',
            cursor: 'pointer',
            WebkitTextFillColor: !selectedRange ? '#9B959F' : '#00010E',
            opacity: !disableTimeRange ? 1 : 0.3,
            fontSize: 14,
            lineHeight: '22px',
            backgroundColor: 'white',
            minHeight: '22px',
            borderTopRightRadius: '5px',
            borderBottomRightRadius: '5px',
            border: '1px solid rgba(155,149,159,0.26)',
            paddingLeft: '10px',
            paddingRight: '10px'
          }
        }}
        value={displayedValue}
      ></Input>
      {(pickerOpen || error) && (
        <NewTimeRange
          handleChange={handleChange}
          startTime={selectedRange.time_after}
          endTime={selectedRange.time_before}
          handleError={setError}
          setOpen={setPickerOpen}
          clear={handleClear}
        />
      )}
    </Box>
  );
};

TimeRangePicker.propTypes = {
  filterType: PropTypes.string.isRequired,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  contextFilters: PropTypes.object.isRequired,
  setContextFilters: PropTypes.func.isRequired
};
